import { Alert, Button } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import type { Note as NoteEntity } from 'sr-types/lib/note/v1/graphql';
import { UserContext } from '../../../../auth/UserContext';
import Loading from '../../../../components/Loading';
import { BUTTON_VARIANTS } from '../../../../constants/constants';
import { useDeepLinkURL } from '../../../../deepLink';
import I18n from '../../../../i18n/I18n';
import { H, ScrollableLayout, V } from '../../../../Layout';
import { ShareSettings, shareSettingsDataToShareSettingsViewModel, useShareSettings } from '../../../../share';
import { DEFAULT_SHARE_SETTINGS, SHARE_SETTINGS_MODE } from '../../../../share/constants';
import { ShareSettingsMode } from '../../../../share/types';
import { failedToastMessage, successToastMessage } from '../../../../utils/commonUtils';
import { getNote, client as notesClient } from '../../../data/notesGraphqlApi';
import { useGetNote } from '../../../data/useGetNoteApi';
import { useNotesContext } from '../../hooks/useNotesContext';
import { useNotesSharePanel } from '../../hooks/useNotesSharePanel';
import { makeExistingNoteShareSettingsScopeId, NEW_NOTE_SHARE_SETTINGS_SCOPE_ID } from '../../shareSettingsScope';

type NoteShareSettingsProps = {
    onAfterExistingShareSettingsSave?: () => Promise<unknown>;
    deepLinkUIStatePath: string;
};

export default function NoteShareSettings({
    onAfterExistingShareSettingsSave,
    deepLinkUIStatePath
}: NoteShareSettingsProps) {
    const { closeSharePanel, openShareForNoteId } = useNotesSharePanel();
    const { userProfile } = useContext(UserContext);
    const { getDeepLinkURL } = useDeepLinkURL();

    const [shareSettingsMode, shareSettingsScopeId]: [ShareSettingsMode, string] = useMemo(
        () =>
            openShareForNoteId
                ? [SHARE_SETTINGS_MODE.update, makeExistingNoteShareSettingsScopeId(openShareForNoteId)]
                : [SHARE_SETTINGS_MODE.create, NEW_NOTE_SHARE_SETTINGS_SCOPE_ID],
        [openShareForNoteId]
    );

    const { shareSettingsForNoteBeingEdited, setShareSettingsForNoteBeingEdited, setPendingShareSettingsForNewNote } =
        useNotesContext();

    const shouldFetchNoteDetails = useMemo(
        () => shareSettingsMode === SHARE_SETTINGS_MODE.update && shareSettingsForNoteBeingEdited === null,
        [shareSettingsForNoteBeingEdited, shareSettingsMode]
    );

    const { noteDetails, isNoteLoading, noteFetchError } = useGetNote(
        openShareForNoteId || '',
        !shouldFetchNoteDetails
    );
    const fetchedNote = noteDetails?.note as NoteEntity | undefined;

    useEffect(() => {
        return () => {
            setShareSettingsForNoteBeingEdited(null);
        };
    }, [setShareSettingsForNoteBeingEdited]);

    useEffect(() => {
        if (!shouldFetchNoteDetails || isNoteLoading || noteFetchError || !fetchedNote) {
            return;
        }

        const { restricted = false, sharedWithAccounts = [], sharedWithDepartments = [], createdBy, createdByName } =
            fetchedNote;
        const noteShareSettingsData: Parameters<typeof shareSettingsDataToShareSettingsViewModel>[0] = {
            restricted,
            sharedWithAccounts,
            sharedWithDepartments,
            entityCreatedBy: createdBy,
            entityCreatedByName: createdByName ?? undefined
        };
        const noteShareSettingsViewModel = shareSettingsDataToShareSettingsViewModel(noteShareSettingsData);

        setShareSettingsForNoteBeingEdited(noteShareSettingsViewModel);
    }, [fetchedNote, isNoteLoading, noteFetchError, setShareSettingsForNoteBeingEdited, shouldFetchNoteDetails]);

    const {
        getShareSettings,
        saveShareSettings,
        isSavingShareSettings,
        checkHasUnsavedShareSettings,
        checkHasUncommittedShareSettings,
        commitCreatedShareSettings
    } = useShareSettings();

    const shareSettings = getShareSettings(shareSettingsScopeId);
    const hasUnsavedChanges =
        shareSettingsMode === SHARE_SETTINGS_MODE.update && checkHasUnsavedShareSettings(shareSettingsScopeId);
    const hasUncommittedChanges =
        shareSettingsMode === SHARE_SETTINGS_MODE.create && checkHasUncommittedShareSettings(shareSettingsScopeId);

    const shouldRefetchNotesListAfterSave = useMemo(() => {
        if (shareSettingsMode !== SHARE_SETTINGS_MODE.update || !userProfile?.accountId) {
            return false;
        }

        const accountId = userProfile.accountId;
        const initialSelfShareAccess = shareSettingsForNoteBeingEdited?.sharedWith?.find(
            (shareAccess) => shareAccess.actor.id === accountId
        );
        const currentSelfShareAccess = shareSettings?.sharedWith?.find(
            (shareAccess) => shareAccess.actor.id === accountId
        );

        return initialSelfShareAccess?.action !== currentSelfShareAccess?.action;
    }, [
        shareSettingsMode,
        shareSettingsForNoteBeingEdited?.sharedWith,
        shareSettings?.sharedWith,
        userProfile?.accountId
    ]);

    const handleSave = useCallback(async () => {
        if (shareSettingsMode === SHARE_SETTINGS_MODE.update) {
            let didSaveSucceed = false;

            try {
                const deepLinkURL = getDeepLinkURL(deepLinkUIStatePath, { id: openShareForNoteId });
                await saveShareSettings(shareSettingsScopeId, { id: openShareForNoteId }, deepLinkURL);
                didSaveSucceed = true;
                successToastMessage('notes.share.settings.updated.success');
                closeSharePanel();
            } catch {
                failedToastMessage('notes.toast.save.failure');
            }

            if (didSaveSucceed) {
                try {
                    await notesClient.refetchQueries({
                        include: [getNote]
                    });

                    if (shouldRefetchNotesListAfterSave) {
                        await onAfterExistingShareSettingsSave?.();
                    }
                } catch (refetchError) {
                    console.warn('Failed to refetch note query after saving share settings', refetchError);
                }
            }
        } else {
            commitCreatedShareSettings(shareSettingsScopeId);
            setPendingShareSettingsForNewNote(shareSettings ?? DEFAULT_SHARE_SETTINGS);
            successToastMessage('notes.share.settings.committed.success');
            closeSharePanel();
        }
    }, [
        closeSharePanel,
        commitCreatedShareSettings,
        deepLinkUIStatePath,
        getDeepLinkURL,
        onAfterExistingShareSettingsSave,
        openShareForNoteId,
        saveShareSettings,
        setPendingShareSettingsForNewNote,
        shareSettings,
        shareSettingsMode,
        shareSettingsScopeId,
        shouldRefetchNotesListAfterSave
    ]);

    if (shouldFetchNoteDetails && isNoteLoading) {
        return (
            <V sx={{ height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <Loading />
            </V>
        );
    }

    if (shouldFetchNoteDetails && (noteFetchError || !fetchedNote)) {
        return (
            <V gap={1} sx={{ justifyContent: 'center', alignItems: 'flex-end' }}>
                <Alert severity="error">
                    <I18n token="notes.share.settings.load.failure" />
                </Alert>
                <Button variant={BUTTON_VARIANTS.text} onClick={closeSharePanel}>
                    <I18n token="dialog.back" />
                </Button>
            </V>
        );
    }

    return (
        <V data-testid={'shareSettings'} sx={{ maxHeight: '100%' }}>
            <ScrollableLayout sx={{ p: 1 }}>
                {shareSettingsMode === SHARE_SETTINGS_MODE.update ? (
                    shareSettingsForNoteBeingEdited && (
                        <ShareSettings
                            mode={SHARE_SETTINGS_MODE.update}
                            scopeId={shareSettingsScopeId}
                            initialShareSettings={shareSettingsForNoteBeingEdited}
                        />
                    )
                ) : (
                    <ShareSettings mode={SHARE_SETTINGS_MODE.create} scopeId={shareSettingsScopeId} />
                )}
            </ScrollableLayout>
            {(shareSettingsMode === SHARE_SETTINGS_MODE.create || shareSettingsForNoteBeingEdited) && (
                <H sx={{ justifyContent: 'flex-end', p: 1 }}>
                    <Button
                        variant={BUTTON_VARIANTS.text}
                        sx={{ mr: 2 }}
                        onClick={closeSharePanel}
                        disabled={isSavingShareSettings}
                    >
                        <I18n token="dialog.cancel" />
                    </Button>
                    <Button
                        variant={BUTTON_VARIANTS.contained}
                        onClick={handleSave}
                        disabled={!(hasUnsavedChanges || hasUncommittedChanges) || isSavingShareSettings}
                    >
                        <I18n token="form.button.save" />
                    </Button>
                </H>
            )}
        </V>
    );
}
